import ExamSocLink from '../soc-link/index.vue'
import DateMixin from '@/shared/mixins/date.mixin'
import ExamMixin from '@/shared/mixins/exam.mixin'
import CONSTANTS from '@/shared/constants/constants'
import { resolvePatientDisplayNames } from '@/shared/utils/patient-name.helper'
import PatientSocialNameInfoIcon from '@/components/patient-social-name-info-icon/index.vue'

const SOCIAL_NAME_MAX_LENGTH = 100

export default {
  name: 'exam-details-header',
  components: {
    ExamSocLink,
    PatientSocialNameInfoIcon
  },
  props: {
    details: {
      type: Object,
      required: true
    },
    statusIcon: {
      type: String,
      required: true
    }
  },
  mixins: [
    DateMixin,
    ExamMixin
  ],
  data() {
    return {
      editPatientName: false,
      editablePatientName: this.details.patientName,
      originalPatientName: this.details.patientName,
      isEditingSocialName: false,
      isConfirmingSocialNameRemoval: false,
      draftSocialName: '',
      isSavingSocialName: false
    }
  },
  watch: {
    editablePatientName(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.editablePatientName = newVal.toUpperCase()
      }
    },
    details: {
      deep: true,
      handler() {
        if (!this.editPatientName) {
          this.editablePatientName = this.civilName
          this.originalPatientName = this.civilName
        }
      }
    }
  },
  computed: {
    age () {
      return this.getDifferenceFromToday(this.details.patient.birthday, 'years')
    },
    gender () {
      const gender = this.details.patient.gender
      if (!gender) {
        return
      }

      if (gender.toLowerCase().startsWith('m')) {
        return this.$i18n.t('sex.male')
      }

      return this.$i18n.t('sex.female')
    },
    socialNameContext () {
      return resolvePatientDisplayNames({
        patientName: this.details.patient?.name || this.details.patientName || '',
        socialName: this.details.patient?.socialName || this.details.socialName
      })
    },
    currentSocialName () {
      return this.socialNameContext.normalizedSocialName
    },
    civilName () {
      return this.details.patient?.name || this.details.patientName || ''
    },
    hasSocialName () {
      return this.socialNameContext.hasSocialName
    },
    primaryDisplayName () {
      return this.hasSocialName ? this.currentSocialName : this.civilName
    },
    isSocialNameConfirmDisabled () {
      const trimmedDraft = this.draftSocialName.trim()
      const hasChanges = trimmedDraft !== this.currentSocialName
      return this.isSavingSocialName || !hasChanges || trimmedDraft.length > SOCIAL_NAME_MAX_LENGTH
    },
    genderFieldLabel () {
      return this.hasSocialName ? `${this.$t('birth_sex')}:` : this.$t('field.gender')
    }
  },
  methods: {
    yearMessage () {
      const verify = this.age >= 2 ? 2 : 1
      return this.$i18n.tc('time.year', verify, { amount: this.age })
    },
    enableEditName () {
      this.editPatientName = true
      this.editablePatientName = this.civilName
      this.originalPatientName = this.civilName
    },
    cancelEditName () {
      this.editPatientName = false
      this.editablePatientName = this.originalPatientName
    },
    async savePatientName () {
      if (this.editablePatientName !== this.originalPatientName) {
        try {
          const response = await this.updatePatientName(this.details.examId, this.editablePatientName)
          if (response.status === 200 || response.status === 202) {
            this.details.patientName = this.editablePatientName
            if (this.details.patient) {
              this.details.patient.name = this.editablePatientName
            }
            this.$root.$emit('toast', { message: this.$t('name_updated_success'), type: 'success' })
          } else {
            this.$root.$emit('toast', { message: this.$t('patient_name_update_error'), type: 'error' })
          }
        } catch (error) {
          console.error(this.$t('error_updating_patient_name'), error)
          this.$root.$emit('toast', { message: this.$t('server_communication_error'), type: 'error' })
        }
      }
      this.editPatientName = false
    },
    startSocialNameEditing () {
      this.isConfirmingSocialNameRemoval = false
      this.draftSocialName = this.currentSocialName
      this.isEditingSocialName = true
    },
    cancelSocialNameEditing () {
      if (this.isSavingSocialName) return
      this.isEditingSocialName = false
      this.draftSocialName = ''
    },
    async saveSocialName (socialName) {
      const patientId = this.details.patient?.id
      if (!patientId) {
        this.$root.$emit('toast', {
          message: this.$t('social_name_missing_patient_id'),
          type: 'error'
        })
        return
      }

      this.isSavingSocialName = true
      try {
        const response = await this.updatePatientSocialName(patientId, {
          socialName
        })

        if (response.status !== 204 && response.status !== 200) {
          throw new Error(`Unexpected status: ${response.status}`)
        }

        const trimmedSocialName = socialName.trim()

        if (this.details.patient) {
          this.$set(this.details.patient, 'socialName', trimmedSocialName)
        }
        this.$set(this.details, 'socialName', trimmedSocialName)

        this.$emit('socialNameUpdated', {
          examId: this.details.examId,
          socialName: trimmedSocialName
        })

        this.$root.$emit('toast', {
          message: this.$t('social_name_updated_success'),
          type: 'success'
        })
        this.isEditingSocialName = false
        this.isConfirmingSocialNameRemoval = false
        this.draftSocialName = ''
      } catch (error) {
        console.error(this.$t('social_name_update_error'), error)
        this.$root.$emit('toast', {
          message: this.$t('social_name_update_error'),
          type: 'error'
        })
      } finally {
        this.isSavingSocialName = false
      }
    },
    async confirmSocialNameSave () {
      const trimmedSocialName = this.draftSocialName.trim()
      if (trimmedSocialName.length > SOCIAL_NAME_MAX_LENGTH) return

      await this.saveSocialName(trimmedSocialName)
    },
    startSocialNameRemoval () {
      if (!this.hasSocialName || this.isSavingSocialName) return

      this.isConfirmingSocialNameRemoval = true
    },
    cancelSocialNameRemoval () {
      if (this.isSavingSocialName) return

      this.isConfirmingSocialNameRemoval = false
    },
    async confirmSocialNameRemoval () {
      if (!this.hasSocialName || this.isSavingSocialName) return

      await this.saveSocialName('')
    },
    canEditPatientName () {
      const now = new Date()
      const creationDate = new Date(this.details.creationDate)
      const hoursDiff = (now - creationDate) / (1000 * 3600)

      const nonEditableStatuses = [
        CONSTANTS.STATUS.AGUARDANDO_LAUDO,
        CONSTANTS.STATUS.AGUARDANDO_INFORMACAO,
        CONSTANTS.STATUS.AGUARDANDO_OBSERVACAO,
        CONSTANTS.STATUS.LAUDO_LIBERADO
      ]

      const currentStatus = this.details.status.id || this.details.status

      return hoursDiff <= 48 && !nonEditableStatuses.includes(currentStatus)
    }
  }
}
